'use client';

import { useMemo, useState } from 'react';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  LayoutList,
  Lock,
  Plus,
  RotateCcw,
  ShieldCheck,
  X,
} from 'lucide-react';
import type { FinderOpportunity } from '@/lib/types';
import { fmtDay, parseMp } from '@/lib/format';
import { serveShift } from './ServeCard';

/**
 * THE TEAM, THEN THE TIMES.
 *
 * Christmas Eve at Troy is a hundred roles across seven services. Listed by
 * day they were one long column of "Ushers · 12/24 4 pm". When a
 * collection's roles carry a Serve Team (serve-teams-1.0.sql), the help step
 * asks what kind of help first, then when (Colton, 2026-10-16):
 *
 *   Team    asked on the sheet's way step (CollectionWho), a multiselect of
 *           teams with Select all, each card with its dates and what is
 *           open. This step then shows every ticked team, one after another.
 *           Reached without that step (a role tapped on the page), it falls
 *           back to a card per team and "See everything".
 *   Times   every day the team runs, shown at once, each a grid of roles
 *           down the side and start times across: the service a role serves
 *           (Ushers at the 4 pm), else when its shift starts (Setup at 9 am),
 *           so setup looks like every other team. A shift with no start time
 *           is a row under the grid.
 *
 * Day comes from the service when there is one (addToEventStart), else the
 * shift's date. A role with no team falls under "More ways to help".
 */

const ANY = 'any';
const NO_TEAM = -1;
/** The "See everything" card: every team's tables, one scroll. */
const EVERYTHING = -2;

/**
 * STICKY HEADINGS, IN THREE LAYERS (Colton, 2026-10-16). Under the person
 * ("What is Colton helping with?", CollectionWho's PERSON_BAR, so nobody
 * signing up a family loses track of whose shifts these are), the team's
 * name sticks, and under it the day's row (WED, DEC 23 · 1 pm · 4 pm · 7 pm)
 * sticks until Thursday's row pushes it off; the team name gives way at the
 * next team. Frosted glass rather than a flat fill, so rows soften as they
 * pass under. Sticky works
 * here because the sheet's body is its own scroll box (Salient's
 * body{overflow:hidden} only kills sticky against the page). Both bleed over
 * the sheet's p-5 / sm:p-6 gutter so rows never show through at the sides.
 * The team bar is a fixed height so the day row knows where to sit.
 */
export const STICK_BG =
  'bg-[color-mix(in_srgb,var(--color-ground,#f5f6f7)_78%,transparent)] backdrop-blur-md backdrop-saturate-150';
export const BLEED = '-mx-5 px-5 sm:-mx-6 sm:px-6';
/** The person bar above both, in CollectionWho. */
export const PERSON_BAR = 'h-16';
const TEAM_BAR = 'h-14 top-16';
const UNDER_TEAM_BAR = 'top-[7.5rem]';

export function hasTeams(roles: FinderOpportunity[]): boolean {
  return roles.some((o) => o.serveTeam);
}

const dayOf = (o: FinderOpportunity) => (o.addToEventStart ?? o.date ?? '').slice(0, 10) || null;

/** "4 pm", "11 am", "7:30 pm" from MP wall clock. */
export function serviceTime(value: string): string {
  const d = parseMp(value);
  const h = d.getHours();
  const m = d.getMinutes();
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'am' : 'pm'}`;
}

interface Team {
  id: number;
  name: string;
  description: string | null;
  sort: number;
}

const teamOf = (o: FinderOpportunity): Team =>
  o.serveTeam ?? { id: NO_TEAM, name: 'More ways to help', description: null, sort: 1e9 };

const bucketOf = (o: FinderOpportunity) => dayOf(o) ?? ANY;

/**
 * The grid column a shift sits in: the service it serves when it serves one
 * (Ushers at the 4 pm), otherwise when the shift itself starts (Setup at 9 am).
 * Same "YYYY-MM-DDTHH:MM:SS" shape both ways, so a 4 pm dinner shift and the
 * 4 pm service share a column. No date or no start time: no column, a row.
 */
function slotOf(o: FinderOpportunity, byService = true): string | null {
  if (byService && o.addToEventStart) return o.addToEventStart;
  if (!o.date || !o.shiftStart || o.shiftStart === '00:00:00') return o.addToEventStart ?? null;
  return `${o.date.slice(0, 10)}T${o.shiftStart.slice(0, 8)}`;
}

/**
 * When a shift actually runs, as epoch ms: its date with its shift times, an
 * end at or before the start rolling past midnight (the 11 pm greeters run
 * 10 pm to 12:10 am). No shift times: the date and its duration. Undated:
 * nothing to clash with.
 */
function interval(o: FinderOpportunity): [number, number] | null {
  if (!o.date) return null;
  const day = o.date.slice(0, 10);
  const hm = (t: string | null) => (t && t !== '00:00:00' ? t.slice(0, 5) : null);
  const s = hm(o.shiftStart);
  const e = hm(o.shiftEnd);
  const start = parseMp(s ? `${day}T${s}:00` : o.date).getTime();
  let end = e ? parseMp(`${day}T${e}:00`).getTime() : start + (o.durationHours ?? 0) * 3_600_000;
  if (end <= start) end += 24 * 3_600_000;
  return end > start ? [start, end] : null;
}
const sortDays = (keys: string[]) =>
  keys.sort((a, b) => (a === ANY ? 1 : b === ANY ? -1 : a.localeCompare(b)));

/** "Dec 23", "Dec 23 and 24", "Nov 14 to Jan 5" */
function span(list: FinderOpportunity[]): string {
  const keys = sortDays([...new Set(list.map(bucketOf))]).filter((d) => d !== ANY);
  if (!keys.length) return 'Any time';
  const md = (d: string) => fmtDay(parseMp(`${d}T00:00:00`)).replace(/^\w+, /, '');
  const first = md(keys[0]);
  if (keys.length === 1) return first;
  const last = md(keys[keys.length - 1]);
  const sameMonth = first.split(' ')[0] === last.split(' ')[0];
  const tail = sameMonth ? last.split(' ').slice(1).join(' ') : last;
  return `${first} ${keys.length === 2 ? 'and' : 'to'} ${tail}`;
}

export interface TeamSummary extends Team {
  /** When it runs, for the card */
  when: string;
  /** Spots still open across its roles */
  open: number;
  roleIds: number[];
}

/**
 * A collection's teams, in order: on a serve page these ARE the registration
 * types, so the Who card's pills and the "How is Colton helping?" step use
 * them in place of a lone "Volunteer".
 */
export function teamSummaries(roles: FinderOpportunity[]): TeamSummary[] {
  const byTeam = new Map<number, { team: Team; list: FinderOpportunity[] }>();
  for (const o of roles) {
    const t = teamOf(o);
    const entry = byTeam.get(t.id) ?? { team: t, list: [] };
    entry.list.push(o);
    byTeam.set(t.id, entry);
  }
  return [...byTeam.values()]
    .map(({ team, list }) => ({
      ...team,
      when: span(list),
      open: list.reduce((n, o) => n + (o.remaining ?? 0), 0),
      roleIds: list.map((o) => o.id),
    }))
    .sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));
}

export function TeamPicker({
  roles,
  label,
  picked,
  holds,
  onToggle,
  teamIds = [],
  cppOk = true,
  cpp = null,
  dropped = [],
  onDrop,
  changeable = () => false,
}: {
  /** This person's campus's roles */
  roles: FinderOpportunity[];
  /** The role's name with its date and time taken off ("Ushers") */
  label: (o: FinderOpportunity) => string;
  picked: number[];
  /** Already signed up */
  holds: (roleId: number) => boolean;
  onToggle: (roleId: number) => void;
  /**
   * The teams ticked on the step before, shown one after another. Empty (the
   * step was skipped, e.g. a role tapped on the page) falls back to the list
   * of teams to open one at a time.
   */
  teamIds?: number[];
  /** This person holds a current CPP; roles with cppRequired are locked otherwise */
  cppOk?: boolean;
  /** "Let Caleb know": asked already, and how to ask (one email for all locked roles) */
  cpp?: {
    asked: boolean;
    sending: boolean;
    error: string | null;
    ask: (ids: number[]) => void;
  } | null;
  /** Held shifts marked to drop on this save (serve-changes-1.0.sql) */
  dropped?: number[];
  /** Mark a held shift to drop, or keep it again */
  onDrop?: (roleId: number) => void;
  /** A held shift can still be cancelled or changed online */
  changeable?: (roleId: number) => boolean;
}) {
  // Held and staying, or held and being dropped on this save.
  const kept = (id: number) => holds(id) && !dropped.includes(id);
  const dropping = (id: number) => holds(id) && dropped.includes(id);
  const canDrop = (id: number) => !!onDrop && changeable(id);
  const teams = useMemo(
    () =>
      [...new Map(roles.map((o) => [teamOf(o).id, teamOf(o)])).values()].sort(
        (a, b) => a.sort - b.sort || a.name.localeCompare(b.name)
      ),
    [roles]
  );
  const [teamId, setTeamId] = useState<number | null>(null);
  const chosen = teams.filter((t) => teamIds.includes(t.id));
  const everything = teamId === EVERYTHING && teams.length > 1;
  const team = everything
    ? null
    : (teams.find((t) => t.id === teamId) ?? (teams.length === 1 ? teams[0] : null));
  const rolesOf = (t: Team) => roles.filter((o) => teamOf(o).id === t.id);
  const daysOf = (list: FinderOpportunity[]) => sortDays([...new Set(list.map(bucketOf))]);

  const pickedIn = (list: FinderOpportunity[]) => list.filter((o) => picked.includes(o.id)).length;

  // One person, one place at a time (Colton, 2026-10-16). A shift that
  // overlaps one they picked, or one they hold that can still change, can be
  // swapped for it after a confirm; one that overlaps a held shift whose
  // changes have closed stays shut.
  const taken = roles
    .filter((o) => picked.includes(o.id) || kept(o.id))
    .map((o) => ({ o, at: interval(o) }));
  const conflicts = (o: FinderOpportunity) => {
    const at = interval(o);
    if (!at) return [];
    return taken
      .filter((t) => t.o.id !== o.id && t.at && t.at[0] < at[1] && at[0] < t.at[1])
      .map((t) => t.o);
  };
  const clashes = (o: FinderOpportunity) => conflicts(o).length > 0;
  // CPP (serve-cpp-1.0.sql): shown, never pickable, for anyone not cleared.
  const locked = (o: FinderOpportunity) => !!o.cppRequired && !cppOk && !holds(o.id);
  const lockedIds = roles.filter(locked).map((o) => o.id);
  const lockedOut = (o: FinderOpportunity) =>
    conflicts(o).some((c) => kept(c.id) && !canDrop(c.id));
  const [swap, setSwap] = useState<{ want: FinderOpportunity; drop: FinderOpportunity[] } | null>(
    null
  );
  const tap = (o: FinderOpportunity) => {
    if (holds(o.id)) {
      if (canDrop(o.id)) onDrop!(o.id);
      setSwap(null);
      return;
    }
    if (!picked.includes(o.id) && clashes(o)) {
      setSwap({ want: o, drop: conflicts(o) });
      return;
    }
    setSwap(null);
    onToggle(o.id);
  };
  /** "Ushers · Wed, Dec 23 · 4 pm" */
  const pickName = (o: FinderOpportunity) =>
    [
      label(o),
      o.date ? fmtDay(parseMp(o.date)) : null,
      o.date ? serviceTime(o.addToEventStart ?? o.date) : null,
    ]
      .filter(Boolean)
      .join(' · ');
  // Held shifts whose online changes have closed: who to ask instead.
  const closedLine = (list: FinderOpportunity[]) => {
    if (!list.length || !onDrop) return null;
    const who = [...new Set(list.map((o) => o.contact?.name?.split(' ')[0]).filter(Boolean))];
    return (
      <p className="text-xs text-gray-500">
        Changes have closed
        {list.length < roles.filter((o) => holds(o.id)).length ? ' for the locked ones' : ''}.{' '}
        {who.length ? `Ask ${who.join(' or ')}` : 'Ask the team'}.
      </p>
    );
  };
  const openIn = (list: FinderOpportunity[]) => list.reduce((n, o) => n + (o.remaining ?? 0), 0);

  const dayLabel = (d: string) => (d === ANY ? 'Any time' : fmtDay(parseMp(`${d}T00:00:00`)));

  const cell = (o: FinderOpportunity | undefined, rowLabel: string) => {
    if (!o) return <span className="block h-11 w-full" aria-hidden />;
    const has = kept(o.id);
    const gone = dropping(o.id);
    const on = picked.includes(o.id) || has;
    const full = o.remaining === 0 && !on && !gone;
    const lock = locked(o);
    const clash = !on && !gone && !full && !lock && clashes(o);
    const shut = (has && !canDrop(o.id)) || full || lock || (clash && lockedOut(o));
    const few = o.remaining != null && o.remaining <= 5;
    return (
      <button
        type="button"
        disabled={shut}
        onClick={() => tap(o)}
        aria-pressed={on}
        aria-label={`${rowLabel}${o.date ? ` at ${serviceTime(o.addToEventStart ?? o.date)}` : ''}${
          gone
            ? ', dropping'
            : has
              ? ', signed up'
              : clash
                ? ', overlaps a shift already picked'
                : full
                  ? ', full'
                  : ''
        }`}
        title={clash ? `Overlaps another shift (${serveShift(o)})` : (serveShift(o) ?? undefined)}
        className={`group flex h-11 w-full flex-col items-center justify-center gap-0.5 rounded-lg border text-[0.62rem] leading-none font-semibold transition focus-visible:ring-2 focus-visible:ring-[var(--color-teal)] focus-visible:outline-none ${
          gone
            ? 'border-dashed border-red-300 bg-red-50 text-red-600 hover:shadow-md active:scale-95'
            : on
              ? 'border-[var(--color-teal)] bg-[var(--color-teal)] text-[var(--on-accent,#fff)]'
              : shut || clash
                ? 'border-dashed border-[var(--color-line)] bg-gray-50 text-gray-400'
                : 'border-gray-300 bg-white text-gray-500 hover:border-[var(--color-teal)] hover:bg-[var(--color-teal-soft)] hover:text-[var(--accent-text,var(--color-teal))] hover:shadow-md active:scale-95'
        }`}
      >
        {gone ? (
          <>
            <X className="h-4 w-4" strokeWidth={3} />
            <span>Drop</span>
          </>
        ) : on ? (
          <Check className="h-4 w-4" strokeWidth={3} />
        ) : lock ? (
          <Lock className="h-3.5 w-3.5" strokeWidth={2.5} aria-label="Needs a current CPP" />
        ) : full ? (
          'Full'
        ) : clash ? (
          'Overlaps'
        ) : (
          <>
            <Plus className="h-4 w-4" strokeWidth={2.5} />
            {few && <span>{o.remaining} left</span>}
          </>
        )}
      </button>
    );
  };

  const listRow = (o: FinderOpportunity, showDate: boolean) => {
    const has = kept(o.id);
    const gone = dropping(o.id);
    const on = picked.includes(o.id) || has;
    const full = o.remaining === 0 && !on && !gone;
    const lock = locked(o);
    const clash = !on && !gone && !full && !lock && clashes(o);
    const when = [showDate && o.date ? fmtDay(parseMp(o.date)) : null, serveShift(o)]
      .filter(Boolean)
      .join(' · ');
    return (
      <button
        key={o.id}
        type="button"
        disabled={(has && !canDrop(o.id)) || full || lock || (clash && lockedOut(o))}
        onClick={() => tap(o)}
        className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-[border-color,background-color,box-shadow] ${
          gone
            ? 'border-dashed border-red-300 bg-red-50'
            : on && (!has || canDrop(o.id))
              ? 'border-[var(--color-teal)] bg-[var(--color-teal-soft)]'
              : has || full || clash
                ? 'border-[var(--color-line)] bg-white opacity-55'
                : 'border-[var(--color-line)] bg-white hover:shadow-md'
        }`}
      >
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
            on
              ? 'border-[var(--color-teal)] bg-[var(--color-teal)] text-[var(--on-accent,#fff)]'
              : 'border-[var(--color-line)] bg-white'
          }`}
        >
          {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold text-[#1c2b39]">{label(o)}</span>
          {when && <span className="block text-xs text-gray-500">{when}</span>}
        </span>
        <span className="shrink-0 text-xs font-semibold text-gray-500">
          {gone
            ? 'Dropping'
            : has
              ? 'Signed up'
              : lock
                ? 'Needs CPP'
                : full
                  ? 'Full'
                  : clash
                    ? 'Overlaps'
                    : o.remaining != null
                      ? `${o.remaining} left`
                      : ''}
        </span>
      </button>
    );
  };

  const grid = (list: FinderOpportunity[], heading?: string, stickUnder = true) => {
    // Columns are services only when the day has several: one tear-down
    // after the 7 pm reads as 8:15 pm, its own start, not "7 pm".
    const byService = new Set(list.map((o) => o.addToEventStart).filter(Boolean)).size > 1;
    const slot = (o: FinderOpportunity) => slotOf(o, byService);
    const withService = list.filter((o) => slot(o));
    const loose = list.filter((o) => !slot(o));
    const times = [...new Set(withService.map((o) => slot(o)!))].sort();
    const rows = [...new Set(withService.map(label))];
    const columns = {
      gridTemplateColumns: `minmax(0,1fr) repeat(${times.length}, minmax(2.75rem, 3.5rem))`,
    };
    return (
      <div className="space-y-3">
        {rows.length > 0 && (
          <div
            className={`sticky ${stickUnder ? UNDER_TEAM_BAR : 'top-16'} z-10 ${STICK_BG} ${BLEED} py-2`}
          >
            <div className="grid items-center gap-1.5" style={columns}>
              <span className="kicker min-w-0 truncate text-[#1c2b39]">{heading}</span>
              {times.map((t) => (
                <span key={t} className="text-center text-xs font-bold text-[#1c2b39]">
                  {serviceTime(t)}
                </span>
              ))}
            </div>
          </div>
        )}
        {rows.length > 0 && (
          <div className="grid items-center gap-1.5" style={columns}>
            {rows.map((row) => (
              <div key={row} className="contents">
                <span className="min-w-0 truncate text-sm font-bold text-[#1c2b39]">{row}</span>
                {times.map((t) => (
                  <div key={t}>
                    {cell(
                      withService.find((o) => label(o) === row && slot(o) === t),
                      row
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {loose.length > 0 && (
          <div className="grid gap-1.5">{loose.map((o) => listRow(o, false))}</div>
        )}
        {swap && list.some((o) => o.id === swap.want.id) && (
          <div
            role="alertdialog"
            aria-label="Switch shifts"
            className="space-y-2.5 rounded-xl border border-[var(--color-teal)] bg-white px-3.5 py-3 text-sm shadow-md"
          >
            <p className="text-[#1c2b39]">
              <b>{pickName(swap.want)}</b> overlaps{' '}
              {swap.drop.map((c, i) => (
                <span key={c.id}>
                  {i > 0 ? ' and ' : ''}
                  <b>{pickName(c)}</b> ({serveShift(c)})
                </span>
              ))}
              .
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setSwap(null)} className="btn btn-quiet">
                Keep it
              </button>
              <button
                type="button"
                onClick={() => {
                  for (const c of swap.drop) holds(c.id) ? onDrop!(c.id) : onToggle(c.id);
                  onToggle(swap.want.id);
                  setSwap(null);
                }}
                className="btn btn-primary"
              >
                Switch
              </button>
            </div>
          </div>
        )}
        {/* The grid shows the service or start; this says when to be there. */}
        {withService.some((o) => picked.includes(o.id) || kept(o.id)) && (
          <ul className="space-y-1 rounded-xl bg-[var(--color-teal-soft)] px-3 py-2.5 text-xs">
            {withService
              .filter((o) => picked.includes(o.id) || kept(o.id))
              .map((o) => (
                <li key={o.id} className="flex justify-between gap-3">
                  <b className="text-[#1c2b39]">
                    {label(o)} · {serviceTime(slot(o)!)}
                  </b>
                  <span className="text-gray-600">{serveShift(o)}</span>
                </li>
              ))}
          </ul>
        )}
      </div>
    );
  };

  // Every pick, whichever team it is on, with a way to take it back; and
  // every shift already held, with a way to drop it while changes are open.
  const mine = roles.filter((o) => picked.includes(o.id));
  const held = roles.filter((o) => holds(o.id));
  const chip = (o: FinderOpportunity, tone: 'pick' | 'held' | 'gone') => (
    <span
      key={o.id}
      className={`inline-flex items-center gap-1 rounded-full py-1 pr-1 pl-2.5 text-xs font-semibold ${
        tone === 'gone'
          ? 'bg-red-50 text-red-700'
          : tone === 'held'
            ? 'border border-[var(--color-teal)] bg-white text-[#1c2b39]'
            : 'bg-[var(--color-teal-soft)] text-[#1c2b39]'
      } ${tone === 'held' && !canDrop(o.id) ? 'pr-2' : ''}`}
    >
      <span className={tone === 'gone' ? 'line-through decoration-red-400' : ''}>
        {pickName(o)}
      </span>
      {tone === 'pick' ? (
        <button
          type="button"
          onClick={() => onToggle(o.id)}
          aria-label={`Remove ${pickName(o)}`}
          className="flex h-5 w-5 items-center justify-center rounded-full text-gray-500 transition hover:bg-white hover:text-[#1c2b39]"
        >
          <X className="h-3 w-3" strokeWidth={3} />
        </button>
      ) : tone === 'gone' ? (
        <button
          type="button"
          onClick={() => onDrop?.(o.id)}
          aria-label={`Keep ${pickName(o)}`}
          className="flex h-5 w-5 items-center justify-center rounded-full transition hover:bg-white"
        >
          <RotateCcw className="h-3 w-3" strokeWidth={3} />
        </button>
      ) : canDrop(o.id) ? (
        <button
          type="button"
          onClick={() => onDrop?.(o.id)}
          aria-label={`Drop ${pickName(o)}`}
          className="flex h-5 w-5 items-center justify-center rounded-full text-gray-500 transition hover:bg-red-50 hover:text-red-600"
        >
          <X className="h-3 w-3" strokeWidth={3} />
        </button>
      ) : (
        <Lock
          className="ml-0.5 h-3 w-3 text-gray-400"
          strokeWidth={2.5}
          aria-label="Changes closed"
        />
      )}
    </span>
  );
  const tray =
    mine.length > 0 || held.length > 0 ? (
      <div className="space-y-3">
        {held.length > 0 && (
          <div className="space-y-1.5">
            <div className="kicker">Signed up</div>
            <div className="flex flex-wrap gap-1.5">
              {held.map((o) => chip(o, dropping(o.id) ? 'gone' : 'held'))}
            </div>
            {closedLine(held.filter((o) => !canDrop(o.id)))}
          </div>
        )}
        {mine.length > 0 && (
          <div className="space-y-1.5">
            <div className="kicker">Your picks</div>
            <div className="flex flex-wrap gap-1.5">{mine.map((o) => chip(o, 'pick'))}</div>
          </div>
        )}
      </div>
    ) : null;

  const teamBar = (t: Team) => (
    <div className={`sticky z-20 ${TEAM_BAR} ${STICK_BG} ${BLEED} flex flex-col justify-center`}>
      <div className="truncate font-bold text-[#1c2b39]">{t.name}</div>
      {t.description && <div className="truncate text-xs text-gray-500">{t.description}</div>}
    </div>
  );

  // One line per team that has locked roles; any of them sends the one email.
  const cppLine = (t: Team) => {
    const list = rolesOf(t).filter(locked);
    if (!list.length || !cpp) return null;
    const who =
      list.find((o) => o.cppContact)?.cppContact?.name ?? list[0].contact?.name ?? 'the team';
    const first = who.split(' ')[0];
    const names = [...new Set(list.map(label))];
    const what = list.length === rolesOf(t).length ? `${t.name} roles` : names.join(', ');
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-[var(--color-line)] bg-white px-3.5 py-3 text-sm">
        <ShieldCheck className="h-4 w-4 shrink-0 text-[var(--accent-text,var(--color-teal))]" />
        <span className="min-w-0 flex-1 text-[#1c2b39]">
          {cpp.asked ? (
            <>
              <b>{first} will reach out</b> about your CPP.
            </>
          ) : (
            <>
              <b>{what}</b> {names.length === 1 && what !== `${t.name} roles` ? 'needs' : 'need'} a
              current CPP.
            </>
          )}
        </span>
        {!cpp.asked && (
          <button
            type="button"
            disabled={cpp.sending}
            onClick={() => cpp.ask(lockedIds)}
            className="btn btn-primary !px-4 !py-2 !text-sm"
          >
            {cpp.sending ? 'Sending' : `Let ${first} know`}
          </button>
        )}
        {cpp.error && (
          <span className="w-full text-xs font-semibold text-red-600">{cpp.error}</span>
        )}
      </div>
    );
  };

  const teamTables = (t: Team) => (
    <div className="space-y-6">
      {cppLine(t)}
      {daysOf(rolesOf(t)).map((d) => (
        <div key={d}>
          {grid(
            rolesOf(t).filter((o) => bucketOf(o) === d),
            dayLabel(d)
          )}
        </div>
      ))}
    </div>
  );

  // Ticked on the way step: those teams, stacked, nothing to navigate.
  if (chosen.length)
    return (
      <div className="space-y-4">
        {tray}
        <div className="space-y-9">
          {chosen.map((t) => (
            <section key={t.id} className="space-y-3">
              {teamBar(t)}
              {teamTables(t)}
            </section>
          ))}
        </div>
      </div>
    );

  if (!team && !everything)
    return (
      <div className="space-y-4">
        {tray}
        <div className="grid gap-1.5">
          {teams.length > 1 && (
            <button
              type="button"
              onClick={() => setTeamId(EVERYTHING)}
              className="card lift flex w-full items-center gap-3 p-4 text-left transition [&]:cursor-pointer"
            >
              <LayoutList className="h-5 w-5 shrink-0 text-[var(--accent-text,var(--color-teal))]" />
              <span className="min-w-0 flex-1">
                <span className="block font-bold text-[#1c2b39]">See everything</span>
                <span className="block text-xs text-gray-500">{span(roles)} · Every team</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
            </button>
          )}
          {teams.map((t) => {
            const list = rolesOf(t);
            const n = pickedIn(list);
            const open = openIn(list);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTeamId(t.id)}
                className="card lift flex w-full items-center gap-3 p-4 text-left transition [&]:cursor-pointer"
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-bold text-[#1c2b39]">{t.name}</span>
                  <span className="block text-xs text-gray-500">
                    {span(list)}
                    {t.description ? ` · ${t.description}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-right text-xs font-semibold text-gray-500">
                  {n > 0 ? (
                    <span className="text-[var(--accent-text,var(--color-teal))]">{n} picked</span>
                  ) : open > 0 ? (
                    `${open} open`
                  ) : null}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
              </button>
            );
          })}
        </div>
      </div>
    );

  return (
    <div className="space-y-4">
      {teams.length > 1 && (
        <button
          type="button"
          onClick={() => {
            setTeamId(null);
            setSwap(null);
          }}
          className="flex items-center gap-1 text-sm font-semibold text-[var(--accent-text,var(--color-teal))]"
        >
          <ChevronLeft className="h-4 w-4" />
          All teams
        </button>
      )}
      {tray}
      {everything ? (
        <div className="space-y-9">
          {teams.map((t) => (
            <section key={t.id} className="space-y-3">
              {teamBar(t)}
              {teamTables(t)}
            </section>
          ))}
        </div>
      ) : (
        team && (
          <>
            {teamBar(team)}
            {/* Every day at once, each a small table under its own heading: one
                less tap than day tabs, and the same look for every team. */}
            {teamTables(team)}
          </>
        )
      )}
    </div>
  );
}
